import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Loader2, User, Phone, Mail, MapPin, Calendar,
  Receipt, TrendingUp, Award, ChevronDown, ChevronRight,
  ShoppingBag, DollarSign, Repeat, Clock, Cake, Users as UsersIcon,
  Sparkles, CalendarDays, Armchair, MessageCircle, Send,
  CreditCard as IdCard, Pencil,
} from "lucide-react";

// Símbolos de género (lucide < 0.481 no exporta Mars/Venus)
function Mars({ size = 16, className = "", "aria-hidden": ah }: { size?: number; className?: string; "aria-hidden"?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden={ah}>
      <circle cx="10" cy="14" r="6" />
      <path d="M19 5l-5.4 5.4" /><path d="M19 5h-5" /><path d="M19 5v5" />
    </svg>
  );
}
function Venus({ size = 16, className = "", "aria-hidden": ah }: { size?: number; className?: string; "aria-hidden"?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden={ah}>
      <circle cx="12" cy="9" r="6" />
      <path d="M12 15v7" /><path d="M9 19h6" />
    </svg>
  );
}
import { cliente360Api, Cliente360Venta } from "../../lib/api";
import { ContactarCliente } from "./ContactarCliente";
import { EditarDatosModal } from "./EditarDatosModal";
import { HabitosCruzadosCard } from "./HabitosCruzadosCard";

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function formatUSD(n: string | number): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("es-VE", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(num);
}

// Las fechas que manda el backend (ultimaVisita, fechaNacimiento, etc.) son
// fechas puras (sin hora) serializadas como instante UTC medianoche --
// parsearlas con `new Date(d)` directo las corre un día atrás en cualquier
// navegador con huso horario negativo (ej. Venezuela, UTC-4). Se interpretan
// como medianoche LOCAL en vez de UTC para que el día calendario no se mueva.
function parseFechaLocal(d: string): Date {
  return new Date(d.slice(0, 10) + "T00:00:00");
}

function formatFecha(d?: string | null): string {
  if (!d) return "—";
  return parseFechaLocal(d).toLocaleDateString("es-ES", {
    year: "numeric", month: "long", day: "numeric",
  });
}

function formatFechaCorta(d?: string | null): string {
  if (!d) return "—";
  return parseFechaLocal(d).toLocaleDateString("es-ES", {
    year: "numeric", month: "short", day: "2-digit",
  });
}

function formatHora(h?: string | null): string {
  if (!h) return "";
  return h.substring(0, 5);
}

function diasDesde(d?: string | null): string {
  if (!d) return "";
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const dias = Math.round((hoy.getTime() - parseFechaLocal(d).getTime()) / 86400000);
  if (dias === 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 30) return `hace ${dias} días`;
  if (dias < 365) return `hace ${Math.floor(dias / 30)} meses`;
  return `hace ${Math.floor(dias / 365)} años`;
}

// ────────────────────────────────────────────────────────────
export function Cliente360DetailPage() {
  const { id } = useParams<{ id: string }>();
  const codCliente = Number(id);
  const [contactarOpen, setContactarOpen] = useState(false);
  const [contactarTab, setContactarTab] = useState<"email" | "whatsapp">("email");
  const [editarOpen, setEditarOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["cliente360", "detalle", codCliente],
    queryFn: () => cliente360Api.detalle(codCliente, {
      limitVentas: 50,
      limitTopArticulos: 10,
    }),
    enabled: !!codCliente,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center text-ink-400">
        <Loader2 className="size-6 animate-spin mr-2" />
        Cargando cliente…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-ink-50 p-8">
        <div className="card p-8 max-w-2xl mx-auto text-danger">
          Error: {(error as Error).message}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const nombre = data.basico.nombre?.trim() || `Cliente #${data.basico.codCliente}`;

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-in">

        <Link to="/cliente360"
          className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800 transition-colors mb-5">
          <ArrowLeft className="size-4" />
          Volver a Cliente 360°
        </Link>

        {/* Header */}
        <header className="card p-6 mb-5">
          <div className="flex items-start gap-5">
            <div className="size-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-md shrink-0">
              <User className="size-8" />
            </div>
            <div className="flex-1 min-w-0">
              {/* Botones de comunicacion + Editar */}
              <div className="flex gap-2 float-right ml-3">
                <button
                  onClick={() => setEditarOpen(true)}
                  className="px-3 py-1.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors">
                  <Pencil className="size-3.5" />
                  Editar
                </button>
                {data.basico.email && (
                  <button
                    onClick={() => { setContactarTab("email"); setContactarOpen(true); }}
                    className="px-3 py-1.5 rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors">
                    <Send className="size-3.5" />
                    Email
                  </button>
                )}
                {(data.basico.mobil || data.basico.telefono1) && (
                  <button
                    onClick={() => { setContactarTab("whatsapp"); setContactarOpen(true); }}
                    className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1.5 transition-colors">
                    <MessageCircle className="size-3.5" />
                    WhatsApp
                  </button>
                )}
              </div>
              <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                <h1 className="font-display text-2xl font-bold text-ink-800 truncate">
                  {nombre}
                </h1>
                <span className="font-mono text-xs text-ink-400">#{data.basico.codCliente}</span>
                {data.cumpleanos?.esHoy && (
                  <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-medium px-2 py-1 rounded-full animate-pulse">
                    <Cake className="size-3.5" />¡Cumple HOY!
                  </span>
                )}
                {data.cumpleanos && !data.cumpleanos.esHoy && data.cumpleanos.proximoEnDias !== null && data.cumpleanos.proximoEnDias <= 30 && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs font-medium px-2 py-1 rounded-full">
                    <Cake className="size-3.5" />Cumple en {data.cumpleanos.proximoEnDias} días
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-600">
                {data.basico.mobil && (
                  <a href={`tel:${data.basico.mobil}`} className="inline-flex items-center gap-1.5 hover:text-brand-600">
                    <Phone className="size-3.5" />{data.basico.mobil}
                  </a>
                )}
                {data.basico.telefono1 && data.basico.telefono1 !== data.basico.mobil && (
                  <a href={`tel:${data.basico.telefono1}`} className="inline-flex items-center gap-1.5 hover:text-brand-600">
                    <Phone className="size-3.5 opacity-60" />{data.basico.telefono1}
                  </a>
                )}
                {data.basico.email && (
                  <a href={`mailto:${data.basico.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-600">
                    <Mail className="size-3.5" />{data.basico.email}
                  </a>
                )}
                {(data.basico.poblacion || data.basico.provincia) && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="size-3.5" />
                    {[data.basico.poblacion, data.basico.provincia].filter(Boolean).join(", ")}
                  </span>
                )}
                {data.basico.cif && (
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                    <IdCard className="size-3.5" />{data.basico.cif}
                  </span>
                )}
                {data.basico.sexo && (
                  <span className="inline-flex items-center gap-1.5">
                    {data.basico.sexo === "M" && (
                      <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                        <Mars size={13} aria-hidden /> M
                      </span>
                    )}
                    {data.basico.sexo === "F" && (
                      <span className="inline-flex items-center gap-1 text-pink-600 font-medium">
                        <Venus size={13} aria-hidden /> F
                      </span>
                    )}
                  </span>
                )}
                {data.basico.fechaNacimiento && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="size-3.5" />
                    Nac. {formatFechaCorta(data.basico.fechaNacimiento)}
                    {(() => {
                      const d = parseFechaLocal(data.basico.fechaNacimiento);
                      const hoy = new Date();
                      let edad = hoy.getFullYear() - d.getFullYear();
                      const m = hoy.getMonth() - d.getMonth();
                      if (m < 0 || (m === 0 && hoy.getDate() < d.getDate())) edad--;
                      return edad >= 0 && edad < 150 ? (
                        <span className="ml-1 px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 text-[10px] font-medium">
                          {edad} años
                        </span>
                      ) : null;
                    })()}
                  </span>
                )}
                {/* Edad estimada por cédula (cuando no hay fecha real) */}
                {!data.basico.fechaNacimiento && (data as any).edadEstimada && (
                  <span className="inline-flex items-center gap-1.5" title="Estimación basada en el número de cédula venezolana">
                    <Cake className="size-3.5 text-amber-500" />
                    <span className="text-ink-500">~{(data as any).edadEstimada.edadAprox} años</span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">
                      {(data as any).edadEstimada.rango}
                    </span>
                    <span className="text-[10px] text-ink-400 italic">
                      est. CI {(data as any).edadEstimada.confianza === "alta" ? "" : `(${(data as any).edadEstimada.confianza})`}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Métricas 360 — primaria destacada + 4 secundarias */}
        {data.resumen && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Metric icon={<Receipt className="size-4 text-brand-600" />}
              label="Visitas" value={String(data.resumen.totalVisitas)}
              highlight />
            <Metric icon={<DollarSign className="size-4 text-brand-600" />}
              label="Total gastado" value={formatUSD(data.resumen.totalGastado)} />
            <Metric icon={<TrendingUp className="size-4 text-brand-600" />}
              label="Ticket promedio" value={formatUSD(data.resumen.ticketPromedio)} />
            <Metric icon={<Clock className="size-4 text-brand-600" />}
              label="Última visita" value={formatFechaCorta(data.resumen.ultimaVisita)}
              sub={diasDesde(data.resumen.ultimaVisita)} />
            <Metric icon={<Calendar className="size-4 text-brand-600" />}
              label="Primera visita" value={formatFechaCorta(data.resumen.primeraVisita)} />
          </div>
        )}

        {/* 3 gráficas en fila: HORA, DÍA SEMANA, MES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <ChartBarras
            titulo="Hora de visita"
            icon={<Clock className="size-4 text-brand-600" />}
            data={data.porHora.map((h) => ({ label: `${h.hora}h`, valor: h.visitas, key: h.hora }))}
            sufijo="visitas"
            labelPico={(it) => `${it.label.replace("h", "")}:00`}
          />
          <ChartBarras
            titulo="Día de la semana"
            icon={<CalendarDays className="size-4 text-brand-600" />}
            data={data.porDiaSemana.map((d, i) => ({
              label: DIAS_SEMANA[i] || "",
              valor: d.visitas,
              key: d.dia,
            }))}
            sufijo="visitas"
            labelPico={(it) => it.label}
          />
          <ChartBarras
            titulo="Mes del año"
            icon={<CalendarDays className="size-4 text-brand-600" />}
            data={data.porMes.map((m) => ({ label: MESES[m.mes - 1], valor: m.visitas, key: m.mes }))}
            sufijo="visitas"
            labelPico={(it) => it.label}
          />
        </div>

        {/* Fila 2: comensales + zona favorita */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <ChartHorizontal
            titulo="Comensales por visita"
            icon={<UsersIcon className="size-4 text-brand-600" />}
            data={data.porComensales.slice(0, 10).map((c) => ({
              label: `${c.numComensales} pers.`, valor: c.visitas, key: c.numComensales,
            }))}
            sufijo="visitas"
          />
          <ChartHorizontal
            titulo="Zona favorita"
            icon={<Armchair className="size-4 text-brand-600" />}
            data={data.porZona.map((z) => ({
              label: z.salaNombre || `Sala ${z.salaIcg}`,
              valor: z.visitas,
              key: z.salaIcg,
            }))}
            sufijo="visitas"
          />
        </div>

        {/* Consumo por sucursal */}
        {data.porSucursal && data.porSucursal.length > 0 && (
          <div className="card overflow-hidden mb-5">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-ink-200">
              <MapPin className="size-4 text-brand-600" />
              <h2 className="font-display font-bold text-ink-800">Consumo por sucursal</h2>
            </div>
            <div className="divide-y divide-ink-100">
              {data.porSucursal.map((s) => (
                <div key={s.sucursalId ?? "sin"} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-ink-800 truncate">{s.sucursalNombre}</p>
                    <p className="text-xs text-ink-400">Última: {formatFechaCorta(s.ultimaVisita)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-ink-800">{s.visitas} <span className="text-xs font-normal text-ink-500">visita{s.visitas !== 1 ? "s" : ""}</span></p>
                    <p className="text-xs font-mono text-brand-700">{formatUSD(s.totalGastado)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top productos + Ventas cruzadas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <div className="card overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-ink-200">
              <Award className="size-4 text-brand-600" />
              <h2 className="font-display font-bold text-ink-800">Top 10 productos consumidos</h2>
            </div>
            {data.topProductos.length === 0 ? (
              <div className="p-8 text-center text-ink-400 text-sm">Sin productos</div>
            ) : (
              <div className="divide-y divide-ink-100">
                {data.topProductos.map((p, i) => (
                  <div key={p.codArticulo} className="px-5 py-3 hover:bg-brand-50/30 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`size-7 rounded-md shrink-0 flex items-center justify-center text-xs font-bold
                        ${i === 0 ? "bg-amber-100 text-amber-700"
                          : i === 1 ? "bg-ink-200 text-ink-600"
                          : i === 2 ? "bg-orange-100 text-orange-700"
                          : "bg-ink-100 text-ink-500"}`}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-ink-800 truncate">
                          {p.nombreArticulo}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-ink-500 mt-0.5">
                          <span>{p.vecesPedido}× pedido</span>
                          <span>{p.totalUnidades} und.</span>
                          <span className="font-mono">{formatUSD(p.totalGastado)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <HabitosCruzadosCard data={(data as any).habitosCruzados} legacy={data.ventasCruzadas} />
        </div>

        {/* Modal de comunicacion */}
        <ContactarCliente
          cliente={{
            nombre: data.basico.nombre,
            email: data.basico.email,
            mobil: data.basico.mobil,
            telefono1: data.basico.telefono1,
          }}
          open={contactarOpen}
          onClose={() => setContactarOpen(false)}
          defaultTab={contactarTab}
        />

        {/* Modal de editar datos */}
        <EditarDatosModal
          cliente={{
            codCliente: data.basico.codCliente,
            nombre: data.basico.nombre,
            cif: (data.basico as any).cif,
            email: data.basico.email,
            telefono1: data.basico.telefono1,
            mobil: data.basico.mobil,
            direccion: data.basico.direccion,
            poblacion: data.basico.poblacion,
            fechaNacimiento: data.basico.fechaNacimiento,
            sexo: (data.basico as any).sexo,
            instagram: (data.basico as any).instagram,
            twitter: (data.basico as any).twitter,
            facebook: (data.basico as any).facebook,
            tiktok: (data.basico as any).tiktok,
          }}
          open={editarOpen}
          onClose={() => setEditarOpen(false)}
        />

        {/* Historial */}
        <div className="card overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-ink-200">
            <Repeat className="size-4 text-brand-600" />
            <h2 className="font-display font-bold text-ink-800">Historial de visitas</h2>
            <span className="text-xs text-ink-400">({data.historial.length} más recientes)</span>
          </div>
          {data.historial.length === 0 ? (
            <div className="p-12 text-center text-ink-400">
              <Receipt className="size-12 mx-auto mb-2 opacity-30" />
              <p>Sin compras registradas</p>
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {data.historial.map((v) => <VentaCard key={v.id} venta={v} sucursalNombre={data.porSucursal?.find((s) => s.sucursalId === v.sucursalId)?.sucursalNombre} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CHART: Barras verticales (hora / día / mes)
// Altura en pixeles para que se vea siempre.
// ════════════════════════════════════════════════════════════
type Item = { label: string; valor: number; key: number | string };

function ChartBarras({
  titulo, icon, data, sufijo, labelPico,
}: {
  titulo: string;
  icon: React.ReactNode;
  data: Item[];
  sufijo: string;
  labelPico: (it: Item) => string;
}) {
  const max = Math.max(...data.map((d) => d.valor), 1);
  const total = data.reduce((s, d) => s + d.valor, 0);
  const pico = data.reduce((acc, d) => d.valor > acc.valor ? d : acc, data[0]);
  const MAX_PX = 110; // altura maxima de barra en pixeles

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink-200">
        {icon}
        <h2 className="font-medium text-sm text-ink-800">{titulo}</h2>
        {total > 0 && pico && pico.valor > 0 && (
          <span className="text-xs text-ink-400 ml-auto">
            Pico: <strong className="text-brand-600">{labelPico(pico)}</strong>
          </span>
        )}
      </div>
      <div className="p-4">
        {total === 0 ? (
          <div className="py-8 text-center text-ink-400 text-sm">Sin datos</div>
        ) : (
          <>
            <div className="flex items-end justify-between gap-0.5" style={{ height: MAX_PX + 8 }}>
              {data.map((d) => {
                const h = d.valor > 0 ? Math.max(Math.round((d.valor / max) * MAX_PX), 4) : 1;
                const isPeak = d.key === pico?.key && d.valor > 0;
                return (
                  <div key={String(d.key)} className="flex-1 flex flex-col items-center gap-1 group relative">
                    {d.valor > 0 && (
                      <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-ink-800 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                        {d.valor} {sufijo}
                      </div>
                    )}
                    <div
                      className={`w-full rounded-t transition-all ${
                        isPeak ? "bg-brand-600" : d.valor > 0 ? "bg-brand-300" : "bg-ink-100"
                      }`}
                      style={{ height: `${h}px` }}
                    />
                  </div>
                );
              })}
            </div>
            {/* Labels en X — solo cada cierto tramo si son muchos */}
            <div className="flex justify-between text-[10px] text-ink-400 mt-1.5">
              {data.length <= 12
                ? data.map((d, i) => (
                    <span key={i} className="flex-1 text-center">
                      {d.label}
                    </span>
                  ))
                : (
                    // muchos buckets (24h): muestro cada 4
                    data.map((d, i) => (
                      <span key={i} className="flex-1 text-center">
                        {i % 4 === 0 ? d.label : ""}
                      </span>
                    ))
                  )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// CHART: Barras horizontales (comensales / zonas)
// ════════════════════════════════════════════════════════════
function ChartHorizontal({
  titulo, icon, data, sufijo,
}: {
  titulo: string;
  icon: React.ReactNode;
  data: Item[];
  sufijo: string;
}) {
  const max = Math.max(...data.map((d) => d.valor), 1);
  const total = data.reduce((s, d) => s + d.valor, 0);
  const pico = data.reduce((acc, d) => !acc || d.valor > acc.valor ? d : acc, data[0]);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink-200">
        {icon}
        <h2 className="font-medium text-sm text-ink-800">{titulo}</h2>
        {total > 0 && pico && (
          <span className="text-xs text-ink-400 ml-auto">
            Más frecuente: <strong className="text-brand-600">{pico.label}</strong>
          </span>
        )}
      </div>
      <div className="p-4">
        {total === 0 ? (
          <div className="py-6 text-center text-ink-400 text-sm">Sin datos</div>
        ) : (
          <div className="space-y-1.5">
            {data.map((d) => {
              const pct = (d.valor / max) * 100;
              const isMax = d.key === pico?.key;
              return (
                <div key={String(d.key)} className="flex items-center gap-2">
                  <div className="text-xs font-medium text-ink-700 w-24 truncate shrink-0">
                    {d.label}
                  </div>
                  <div className="flex-1 relative h-5 bg-ink-50 rounded overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${isMax ? "bg-brand-600" : "bg-brand-300"} rounded transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center px-2 text-xs font-medium">
                      <span className={isMax ? "text-white" : "text-ink-700"}>
                        {d.valor} {sufijo}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
function VentaCard({ venta, sucursalNombre }: { venta: Cliente360Venta; sucursalNombre?: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="px-5 py-3 hover:bg-brand-50/20 transition-colors">
      <button onClick={() => setExpanded((e) => !e)} className="w-full flex items-center gap-3 text-left">
        {expanded
          ? <ChevronDown className="size-4 text-ink-400 shrink-0" />
          : <ChevronRight className="size-4 text-ink-400 shrink-0" />}
        <div className="flex-1 grid grid-cols-12 gap-2 items-baseline">
          <div className="col-span-3 text-sm font-medium text-ink-800">
            {formatFecha(venta.fecha)}
            <span className="text-xs text-ink-500 ml-2">{formatHora(venta.hora)}</span>
          </div>
          <div className="col-span-3 text-xs text-ink-500">
            {sucursalNombre && <span className="font-medium text-brand-700">{sucursalNombre}</span>}
            {venta.sala > 0 && <span>{sucursalNombre ? " · " : ""}Sala {venta.sala} · Mesa {venta.mesa}</span>}
            {venta.numComensales > 0 && <span> · {venta.numComensales} pers.</span>}
          </div>
          <div className="col-span-3 text-xs text-ink-500">
            <ShoppingBag className="size-3 inline mr-1" />
            {venta.lineas.length} items
          </div>
          <div className="col-span-3 text-right font-mono font-semibold text-ink-800">
            {formatUSD(venta.totalNeto)}
          </div>
        </div>
      </button>

      {expanded && venta.lineas.length > 0 && (
        <div className="mt-3 ml-7 border-l-2 border-brand-200 pl-4">
          <table className="w-full text-xs">
            <thead className="text-ink-500">
              <tr>
                <th className="text-left font-medium pb-1">Producto</th>
                <th className="text-right font-medium pb-1">Cant.</th>
                <th className="text-right font-medium pb-1">P. unit.</th>
                <th className="text-right font-medium pb-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {venta.lineas.map((l) => (
                <tr key={l.numLin} className="border-t border-ink-100">
                  <td className="py-1 text-ink-700">{l.descripcion}</td>
                  <td className="py-1 text-right font-mono text-ink-600">{l.cantidad}</td>
                  <td className="py-1 text-right font-mono text-ink-600">{formatUSD(l.precio)}</td>
                  <td className="py-1 text-right font-mono text-ink-800">{formatUSD(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Metric({ icon, label, value, sub, highlight }: {
  icon: React.ReactNode; label: string; value: string; sub?: string; highlight?: boolean;
}) {
  return (
    <div className={`card p-4 transition-shadow ${
      highlight
        ? "ring-1 ring-brand-300 bg-gradient-to-br from-brand-50/60 to-white shadow-md"
        : "hover:shadow-sm"
    }`}>
      <div className={`flex items-center gap-1.5 text-xs mb-1 ${
        highlight ? "text-brand-700 font-semibold" : "text-ink-500"
      }`}>
        {icon}{label}
      </div>
      <div className={`font-display font-bold leading-tight ${
        highlight ? "text-brand-700 text-2xl" : "text-ink-800 text-lg"
      }`}>
        {value}
      </div>
      {sub && <div className="text-xs text-ink-400 italic mt-0.5">{sub}</div>}
    </div>
  );
}
